import { computed, effect } from "@preact/signals";
import { arrangeMoments, type ArrangeOperation } from "../format/arrange.ts";
import { commit, put } from "./edits.ts";
import { arrangeSelection, canEdit, cueListOpen, ix, type MarkRef, panel, selected, view } from "./state.ts";

export const arranging = computed(() => arrangeSelection.value !== null);
export const arrangedIds = computed(() => new Set(arrangeSelection.value?.ids));

export function finishArrange(): void {
  arrangeSelection.value = null;
}

export function startArrange(): void {
  if (!canEdit.value || panel.value || view.value.preview) return;
  selected.value = null;
  cueListOpen.value = false;
  arrangeSelection.value = { page: null, ids: [] };
}

export function clearArrange(): void {
  if (arranging.value) arrangeSelection.value = { page: null, ids: [] };
}

export function toggleArrange(ref: MarkRef): void {
  const selection = arrangeSelection.value;
  const index = ix.value;
  if (!selection || !index || ref.kind !== "moment") return;
  const moment = index.moments.get(ref.id);
  if (!moment) return;
  const ids = selection.page === moment.anchor.page ? selection.ids : [];
  const next = ids.includes(ref.id) ? ids.filter((id) => id !== ref.id) : [...ids, ref.id];
  arrangeSelection.value = { page: next.length ? moment.anchor.page : null, ids: next };
}

export function arrange(operation: ArrangeOperation, label: string): void {
  const selection = arrangeSelection.value;
  const index = ix.value;
  if (!selection?.page || !index || !canEdit.value || panel.value || view.value.preview) return;
  const moments = selection.ids.flatMap((id) => {
    const moment = index.moments.get(id);
    return moment ? [moment] : [];
  });
  const changes = arrangeMoments(index.page(selection.page), moments, operation);
  commit(label, changes.map((after) => put("moments", after, index.moments.get(after.id)!)));
}

// Selection cannot retain invisible/deleted records or coexist with an editor/Preview.
effect(() => {
  const selection = arrangeSelection.value;
  const index = ix.value;
  if (!selection) return;
  if (!index || !canEdit.value || panel.value || view.value.preview) {
    finishArrange();
    return;
  }
  const hidden = new Set(view.value.hiddenDepartments);
  const ids = selection.ids.filter((id) => {
    const moment = index.moments.get(id);
    if (!moment || moment.anchor.page !== selection.page) return false;
    const cues = index.cuesOf(id);
    return !cues.length || cues.some((cue) => !hidden.has(cue.department) || !index.departments.has(cue.department));
  });
  if (ids.length !== selection.ids.length) arrangeSelection.value = { page: ids.length ? selection.page : null, ids };
});
